import type { WsInboundEnvelope } from "./wsInboundEnvelope";
import { optBool, optCallerId, optString } from "./wsPayloadCoercion";

export interface CallAnsweredWsPayload {
  type: "call_answered";
  call_id?: string;
  answerer_id?: number;
  answered_on_other_device?: boolean;
  answered_on_this_device?: boolean;
}

export interface CallRingingWsPayload {
  type: "call_ringing";
  call_id?: string;
  target_user_id?: number;
  ws_notified?: boolean;
  ws_in_foreground?: boolean;
  fcm_requested?: boolean;
  fcm_sent?: boolean;
}

export interface SipCallAnsweredWsPayload {
  type: "sip_call_answered";
  call_id?: string;
  call_log_id?: string;
  livekit_room?: string;
  room_name?: string;
  answerer_id?: number;
  answered_on_other_device?: boolean;
  answered_on_this_device?: boolean;
}

export interface SipCallRejectedWsPayload {
  type: "sip_call_rejected";
  call_id?: string;
  call_log_id?: string;
  rejected_on_other_device?: boolean;
}

export type CallEndedOrRejectedWsType = "call_rejected" | "call_ended";

export interface CallEndedOrRejectedWsPayload {
  type: CallEndedOrRejectedWsType;
  call_id?: string;
  reason?: string;
}

export interface CallAnswerFailedWsPayload {
  type: "call_answer_failed";
  reason?: string;
  error?: string;
}

export interface SipCallEndedWsPayload {
  type: "sip_call_ended";
  call_log_id?: string;
  livekit_room?: string;
  room_name?: string;
  call_id?: string;
  was_on_hold?: boolean;
}

export function parseCallAnsweredPayload(
  envelope: WsInboundEnvelope,
): CallAnsweredWsPayload | null {
  if (envelope.type !== "call_answered") return null;
  const o = envelope;
  return {
    type: "call_answered",
    call_id: optString(o.call_id),
    answerer_id: optCallerId(o.answerer_id) ?? optCallerId(o.answered_by),
    answered_on_other_device: optBool(o.answered_on_other_device),
    answered_on_this_device: optBool(o.answered_on_this_device),
  };
}

export function parseCallRingingPayload(
  envelope: WsInboundEnvelope,
): CallRingingWsPayload | null {
  if (envelope.type !== "call_ringing") return null;
  const o = envelope;
  return {
    type: 'call_ringing',
    call_id: optString(o.call_id),
    target_user_id: optCallerId(o.target_user_id),
    ws_notified: optBool(o.ws_notified),
    ws_in_foreground: optBool(o.ws_in_foreground),
    fcm_requested: optBool(o.fcm_requested),
    fcm_sent: optBool(o.fcm_sent),
  };
}

export function parseSipCallAnsweredPayload(
  envelope: WsInboundEnvelope,
): SipCallAnsweredWsPayload | null {
  if (envelope.type !== "sip_call_answered") return null;
  const o = envelope;
  return {
    type: "sip_call_answered",
    call_id: optString(o.call_id),
    call_log_id: optString(o.call_log_id),
    livekit_room: optString(o.livekit_room),
    room_name: optString(o.room_name),
    answerer_id: optCallerId(o.answerer_id),
    answered_on_other_device: optBool(o.answered_on_other_device),
    answered_on_this_device: optBool(o.answered_on_this_device),
  };
}

export function parseSipCallRejectedPayload(
  envelope: WsInboundEnvelope,
): SipCallRejectedWsPayload | null {
  if (envelope.type !== "sip_call_rejected") return null;
  return {
    type: "sip_call_rejected",
    call_id: optString(envelope.call_id),
    call_log_id: optString(envelope.call_log_id),
    rejected_on_other_device: optBool(envelope.rejected_on_other_device),
  };
}

export function parseCallEndedOrRejectedPayload(
  envelope: WsInboundEnvelope,
): CallEndedOrRejectedWsPayload | null {
  const t = envelope.type;
  if (t !== "call_rejected" && t !== "call_ended") return null;
  const o = envelope;
  return {
    type: t,
    call_id: optString(o.call_id),
    reason: optString(o.reason),
  };
}

export function parseCallAnswerFailedPayload(
  envelope: WsInboundEnvelope,
): CallAnswerFailedWsPayload | null {
  if (envelope.type !== "call_answer_failed") return null;
  const o = envelope;
  return {
    type: "call_answer_failed",
    reason: optString(o.reason),
    error: optString(o.error),
  };
}

export function parseSipCallEndedPayload(
  envelope: WsInboundEnvelope,
): SipCallEndedWsPayload | null {
  if (envelope.type !== "sip_call_ended") return null;
  const o = envelope;
  return {
    type: "sip_call_ended",
    call_log_id: optString(o.call_log_id),
    livekit_room: optString(o.livekit_room),
    room_name: optString(o.room_name),
    call_id: optString(o.call_id),
    was_on_hold: optBool(o.was_on_hold),
  };
}
